import { cartItemMergeKey, itemIncluidoPagamento, type ItemCarrinho } from '@caixa/types';

export function quantidadeMilesimos(value: number): number {
  return Math.round(value * 1000);
}

export function removedComandaItemIds(
  baseline: Array<{ cartItemId: string; itemId: number; cozinhaPendente?: boolean }>,
  items: ItemCarrinho[], pendingOnly = false,
): number[] {
  const currentIds = new Set(items.map((item) => item.id));
  const surviving = new Set(baseline.filter((item) => currentIds.has(item.cartItemId)).map((item) => item.itemId));
  return [...new Set(baseline.filter((item) => (!pendingOnly || item.cozinhaPendente)
    && !currentIds.has(item.cartItemId) && !surviving.has(item.itemId) && item.itemId > 0).map((item) => item.itemId))];
}

export function addCartItem(
  items: ItemCarrinho[], incoming: Omit<ItemCarrinho, 'id' | 'desconto'>, id: string,
): ItemCarrinho[] {
  // Decimal additions keep their own IDs so ungrouping/payment never invents portions.
  if (!incoming.permiteDecimal) {
    const key = cartItemMergeKey(incoming);
    const index = items.findIndex((item) => !item.permiteDecimal && cartItemMergeKey(item) === key);
    if (index >= 0) return items.map((item, i) => i === index
      ? { ...item, quantidade: Math.round(item.quantidade + incoming.quantidade), marcado: true }
      : item);
  }
  return [...items, {
    ...incoming, id, desconto: 0, marcado: true,
    quantidade: incoming.permiteDecimal ? quantidadeMilesimos(incoming.quantidade) / 1000 : incoming.quantidade,
  }];
}

export type CartQuantityGroup = { item: ItemCarrinho; ids: string[] };

export function groupCartQuantities(items: ItemCarrinho[], desagrupar: boolean): CartQuantityGroup[] {
  const groups: CartQuantityGroup[] = [];
  const byKey = new Map<string, CartQuantityGroup>();
  for (const item of items) {
    if (desagrupar || !item.permiteDecimal) {
      groups.push({ item, ids: [item.id] });
      continue;
    }
    const key = JSON.stringify([cartItemMergeKey(item), item.precoUnitario, item.promocao_id ?? null]);
    const group = byKey.get(key);
    if (!group) {
      const next = { item: { ...item }, ids: [item.id] };
      groups.push(next);
      byKey.set(key, next);
    } else {
      group.ids.push(item.id);
      group.item.quantidade = (quantidadeMilesimos(group.item.quantidade) + quantidadeMilesimos(item.quantidade)) / 1000;
      group.item.desconto += item.desconto;
      group.item.marcado = itemIncluidoPagamento(group.item) && itemIncluidoPagamento(item);
    }
  }
  return groups;
}

export function updateCartQuantity(items: ItemCarrinho[], id: string, delta: number, groupIds?: string[]): ItemCarrinho[] {
  // A grouped +/- adjusts the newest remaining entry, not every original portion.
  const target = groupIds?.length
    ? [...groupIds].reverse().find((key) => items.some((item) => item.id === key)) ?? id
    : id;
  return items.map((item) => {
    if (item.id !== target) return item;
    const quantidade = item.permiteDecimal
      ? Math.max(0, quantidadeMilesimos(item.quantidade) + delta) / 1000
      : Math.max(0, Math.round(item.quantidade + delta));
    return { ...item, quantidade };
  }).filter((item) => item.quantidade > 0);
}
